import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Collapse,
  IconButton,
  Box,
  Typography,
  Button,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableItem } from '../orderable-table/sortable-item.jsx';

const CollapsibleOrderableTable = ({
  columns,
  subItemsDescription,
  subColumns,
  data,
  subItemsKey,
  rowKey,
  onChangeOrder,
  onChangeNestedOrder,
  onSaveOrderChange,
}) => {
  const [open, setOpen] = useState({});
  const [items, setItems] = useState(data);

  useEffect(() => {
    setItems(data);
  }, [data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleToggle = (id) => {
    setOpen((prevState) => ({ ...prevState, [id]: !prevState[id] }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((item) => item[rowKey] === active.id);
    const newIndex = items.findIndex((item) => item[rowKey] === over.id);
    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);
    onChangeOrder?.(oldIndex, newIndex);
  };

  const handleNestedDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // Find the parent item of the dragged item
    const parentItem = items.find((item) =>
      item[subItemsKey]?.some((sub) => sub[rowKey] === active.id),
    );
    if (!parentItem) return;
    // Find the sub-items of the parent item
    const subItems = parentItem[subItemsKey];
    if (!subItems) return;
    const oldIndex = subItems.findIndex((item) => item[rowKey] === active.id);
    const newIndex = subItems.findIndex((item) => item[rowKey] === over.id);
    // Find the sub-items of the dragged item
    const newSubItems = arrayMove(subItems, oldIndex, newIndex);
    // Update the parent item with the new sub-items
    const newItems = items.map((item) => {
      if (item[rowKey] === parentItem[rowKey]) {
        return { ...item, [subItemsKey]: newSubItems };
      }
      return item;
    });
    // Update the state with the new items
    setItems(newItems);
    onChangeNestedOrder?.(oldIndex, newIndex, parentItem[rowKey]);
  };

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={data.map((item) => item[rowKey])}
          strategy={verticalListSortingStrategy}
        >
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="5%" />
                  {columns.map((col) => (
                    <TableCell key={col.key} width={col.width} align={col.align}>
                      {col.title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row) => (
                  <React.Fragment key={row[rowKey]}>
                    <SortableItem id={row[rowKey]}>
                      {(row[subItemsKey]?.length !== 0 && (
                        <TableCell>
                          <IconButton size="small" onClick={() => handleToggle(row[rowKey])}>
                            {open[row[rowKey]] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          </IconButton>
                        </TableCell>
                      )) || <TableCell />}
                      {columns.map((col) => (
                        <TableCell key={col.key} width={col.width} align={col.align}>
                          {col.render ? col.render(row) : row[col.key]}
                        </TableCell>
                      ))}
                    </SortableItem>
                    {row[subItemsKey] && (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleNestedDragEnd}
                      >
                        <SortableContext
                          items={row[subItemsKey].map((item) => item[rowKey])}
                          strategy={verticalListSortingStrategy}
                        >
                          <TableRow>
                            <TableCell style={{ padding: 0 }} colSpan={subColumns.length + 1}>
                              <Collapse in={open[row[rowKey]]} timeout="auto" unmountOnExit>
                                <Box sx={{ margin: 1 }}>
                                  <Typography
                                    variant="h6"
                                    gutterBottom
                                    component="div"
                                    fontWeight={700}
                                  >
                                    {subItemsDescription}
                                  </Typography>
                                  <Table size="small">
                                    <TableBody>
                                      {row[subItemsKey].map((sub) => (
                                        <SortableItem key={sub[rowKey]} id={sub[rowKey]}>
                                          {subColumns.map((col) => (
                                            <TableCell
                                              key={col.key}
                                              width={col.width}
                                              align={col.align}
                                            >
                                              {col.render ? col.render(sub) : sub[col.key]}
                                            </TableCell>
                                          ))}
                                        </SortableItem>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </SortableContext>
                      </DndContext>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SortableContext>
      </DndContext>
      <Button
        variant="contained"
        color="primary"
        onClick={() => onSaveOrderChange?.(items)}
        sx={{ mt: 2 }}
      >
        Salvar alteração de ordem
      </Button>
    </>
  );
};

CollapsibleOrderableTable.propTypes = {
  columns: PropTypes.array.isRequired,
  subColumns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  subItemsKey: PropTypes.string.isRequired,
  rowKey: PropTypes.string.isRequired,
  subItemsDescription: PropTypes.string,
  onChangeOrder: PropTypes.func,
  onChangeNestedOrder: PropTypes.func,
  onSaveOrderChange: PropTypes.func.isRequired,
};

export default CollapsibleOrderableTable;
